import { useMemo, memo } from "react";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";

interface BatchOrderSummaryProps {
  customers: CustomerTemplate[];
}

function BatchOrderSummaryImpl({ customers }: BatchOrderSummaryProps) {
  const summaryRows = useMemo(() => {
    const summaryMap = new Map<string, { name: string; price: number; totalQuantity: number; totalSales: number }>();

    customers
      .filter(c => c.nickname !== "고객 닉네임")
      .forEach(customer => {
        customer.items.forEach(item => {
          const key = `${item.name}__${item.price}`;
          const existing = summaryMap.get(key);
          if (existing) {
            summaryMap.set(key, {
              name: item.name,
              price: item.price,
              totalQuantity: existing.totalQuantity + item.quantity,
              totalSales: existing.totalSales + item.price * item.quantity,
            });
          } else {
            summaryMap.set(key, { name: item.name, price: item.price, totalQuantity: item.quantity, totalSales: item.price * item.quantity });
          }
        });
      });

    return Array.from(summaryMap.values())
      .sort((a, b) => b.totalQuantity - a.totalQuantity);
  }, [customers]);

  if (summaryRows.length === 0) return null;

  const totalQuantity = summaryRows.reduce((sum, r) => sum + r.totalQuantity, 0);
  const totalSales = summaryRows.reduce((sum, r) => sum + r.totalSales, 0);

  return (
    <div className="rounded-lg border border-indigo-100 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-indigo-50 border-b border-indigo-100">
        <h3 className="text-sm font-semibold text-indigo-700">상품 요약</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          현재 필터 기준 집계. 닉네임이 &apos;고객 닉네임&apos;인 주문은 제외됩니다.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-2 text-left font-medium text-gray-600">상품명</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600">단가</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600">총수량</th>
              <th className="px-4 py-2 text-right font-medium text-gray-600">총매출</th>
            </tr>
          </thead>
          <tbody>
            {summaryRows.map((row, i) => (
              <tr
                key={`${row.name}__${row.price}`}
                className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="px-4 py-2 text-gray-800">{row.name}</td>
                <td className="px-4 py-2 text-right text-gray-600">
                  {row.price.toLocaleString()}원
                </td>
                <td className="px-4 py-2 text-right text-gray-800 font-medium">
                  {row.totalQuantity.toLocaleString()}개
                </td>
                <td className="px-4 py-2 text-right text-gray-800 font-medium">
                  {row.totalSales.toLocaleString()}원
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-indigo-50 border-t-2 border-indigo-200 font-semibold">
              <td className="px-4 py-2 text-indigo-700">
                합계 (총 {summaryRows.length}종)
              </td>
              <td className="px-4 py-2" />
              <td className="px-4 py-2 text-right text-indigo-700">
                {totalQuantity.toLocaleString()}개
              </td>
              <td className="px-4 py-2 text-right text-indigo-700">
                {totalSales.toLocaleString()}원
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export const BatchOrderSummary = memo(BatchOrderSummaryImpl);
